import type { Doc } from "building-types/doc";
import { getFloorAssemblyEntity } from "building-types/floors/floorAssemblyEntityTypes";
import { FloorAssemblyType, type FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { FloorHandle } from "building-types/handles/docEntityHandleTypes";
import { builtInFrameFloorAssemblies } from "./defaults/defaultFloorAssembly";
import { builtInSlabFloorAssemblies } from "./defaults/defaultFloorAssembly";
import { docUpdateEntity } from "./docUpdateEntity";
import { isVoidFloorAssemblyOverride } from "./elements/floor/floorAssembly";
import { docGetTableEntity } from "./entity/docGetTableEntity";

/**
 * Specialized merge function for floor assembly overrides that handles structuralType validation
 * and ensures consistency between structuralType and related fields like spanDirection.
 */
export function docMergeFloorProps(doc: Doc, handle: FloorHandle, props: Partial<FloorAssemblyOverride>): Doc {
  const currentFloorAssemblyOverride = docGetTableEntity(doc, handle) as FloorAssemblyOverride;

  if (isVoidFloorAssemblyOverride(currentFloorAssemblyOverride)) {
    return docUpdateEntity(doc, handle.type, handle.id, props);
  }

  if (!currentFloorAssemblyOverride) {
    console.warn(`Floor entity not found for handle ${handle.id}`);
    return doc;
  }

  const frameFloorAssemblies = { ...doc.frameFloorAssemblies, ...builtInFrameFloorAssemblies };
  const slabFloorAssemblies = { ...doc.slabFloorAssemblies, ...builtInSlabFloorAssemblies };
  const assemblyId = props["assemblyId"];
  const spanDirection = props["spanDirection"];
  const newAssembly = getFloorAssemblyEntity(assemblyId, frameFloorAssemblies, slabFloorAssemblies);

  if (newAssembly) {
    if (newAssembly.entityType === "FRAME_FLOOR_ASSEMBLY") {
      return docUpdateEntity(doc, handle.type, handle.id, {
        ...props,
        structuralType: StructuralFloorType.FRAMING,
        spanDirection: spanDirection ?? newAssembly.spanDirection,
        assemblyType: FloorAssemblyType.SOLID,
      });
    } else {
      return docUpdateEntity(doc, handle.type, handle.id, {
        ...props,
        structuralType: StructuralFloorType.SLAB,
        assemblyType: FloorAssemblyType.SOLID,
      });
    }
  }
  return docUpdateEntity(doc, handle.type, handle.id, props);
}
